// Settings page logic that doesn't need rendering: token catalogue, what counts
// as an unsaved change, what a save sends, and colour contrast.

export const HEX_RE = /^#[0-9a-fA-F]{6}$/

export const isHex = value => typeof value === 'string' && HEX_RE.test(value)

// The colour input returns lower case; the API stores whatever it was given.
export const sameColor = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase()

// `live: false` tokens are applied as CSS variables, but nothing in the web app reads them yet.
export const TOKEN_GROUPS = [
  {
    id: 'buttons',
    label: 'Buttons',
    live: true,
    tokens: [
      { key: 'primary', label: 'Primary' },
      { key: 'primary_hover', label: 'Primary hover' },
      { key: 'primary_text', label: 'Button text' },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    live: true,
    tokens: [
      { key: 'success', label: 'Success' },
      { key: 'warning', label: 'Warning' },
      { key: 'error', label: 'Error' },
      { key: 'info', label: 'Info' },
    ],
  },
  {
    id: 'toggles',
    label: 'Toggles',
    live: true,
    tokens: [
      { key: 'toggle_on', label: 'On', hint: 'Matches brand colour by default' },
      { key: 'toggle_off', label: 'Off' },
    ],
  },
  {
    id: 'other',
    label: 'More colours',
    live: false,
    tokens: [
      { key: 'primary_active', label: 'Pressed' },
      { key: 'background', label: 'Background' },
      { key: 'surface', label: 'Surface' },
      { key: 'border', label: 'Border' },
      { key: 'text_primary', label: 'Text' },
      { key: 'text_secondary', label: 'Secondary text' },
      { key: 'text_inverse', label: 'Inverse text' },
    ],
  },
]

// Every key the branding API accepts in `theme_tokens`. It rejects anything else.
export const TOKEN_KEYS = TOKEN_GROUPS.flatMap(group => group.tokens.map(token => token.key))

export function brandingDraftFrom(branding, defaults) {
  return {
    app_name: branding?.app_name || defaults.app_name,
    subtitle: branding?.subtitle || defaults.subtitle,
    primary_color: branding?.primary_color || defaults.primary_color,
    logo_url: branding?.logo_url || null,
    favicon_url: branding?.favicon_url || null,
    apply_to_plugin: Boolean(branding?.apply_to_plugin),
    plugin_body_text: branding?.plugin_body_text || '',
  }
}

export function tokenDraftFrom(tokens, defaults) {
  return Object.fromEntries(TOKEN_KEYS.map(key => [key, tokens?.[key] || defaults[key]]))
}

// Section ids with unsaved edits. They match the page nav ids.
export function dirtySections({ draft, saved, tokens, savedTokens, prefs, savedPrefs }) {
  const dirty = new Set()

  if (
    draft.app_name !== saved.app_name
    || draft.subtitle !== saved.subtitle
    || !sameColor(draft.primary_color, saved.primary_color)
    || draft.logo_url !== saved.logo_url
    || draft.favicon_url !== saved.favicon_url
  ) dirty.add('branding')

  if (TOKEN_KEYS.some(key => !sameColor(tokens[key], savedTokens[key]))) dirty.add('theme')

  if (
    draft.apply_to_plugin !== saved.apply_to_plugin
    || (!draft.apply_to_plugin && draft.plugin_body_text.trim() !== saved.plugin_body_text.trim())
  ) dirty.add('plugin')

  if (Object.keys(savedPrefs).some(key => prefs[key] !== savedPrefs[key])) dirty.add('display')

  return dirty
}

// Values the API would reject. One bad colour fails the whole PUT, so catch them here.
export function validationErrors({ draft, tokens }) {
  const errors = {}
  if (!draft.app_name.trim()) errors.app_name = 'Enter a name.'
  if (!isHex(draft.primary_color)) errors.primary_color = 'Enter a 6-digit hex, like #1E2A3B.'
  TOKEN_KEYS.forEach(key => {
    if (!isHex(tokens[key])) errors[`token:${key}`] = 'Enter a 6-digit hex.'
  })
  return errors
}

// PUT /branding body for what changed, or null. Logo and favicon are only sent
// once set; resetting is how they get cleared.
export function buildBrandingPayload({ dirty, draft, saved, tokens, tokenDefaults }) {
  const payload = {}

  if (dirty.has('branding') || dirty.has('plugin')) {
    payload.app_name = draft.app_name.trim()
    payload.subtitle = draft.subtitle.trim()
    payload.primary_color = draft.primary_color
    payload.apply_to_plugin = draft.apply_to_plugin
    if (draft.logo_url) payload.logo_url = draft.logo_url
    if (draft.favicon_url) payload.favicon_url = draft.favicon_url
    const body = draft.plugin_body_text.trim()
    if (!draft.apply_to_plugin && body !== saved.plugin_body_text.trim()) {
      payload.plugin_body_text = body || null
    }
  }

  if (dirty.has('theme')) {
    // The API keeps only tokens that differ from its defaults, comparing case
    // sensitively, so send defaults back in the case it knows.
    payload.theme_tokens = Object.fromEntries(TOKEN_KEYS.map(key => [
      key,
      sameColor(tokens[key], tokenDefaults[key]) ? tokenDefaults[key] : tokens[key],
    ]))
  }

  return Object.keys(payload).length ? payload : null
}

// Contrast (WCAG 2.x)

function channel(c) {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(hex) {
  if (!isHex(hex)) return null
  const n = parseInt(hex.slice(1), 16)
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
}

// Ratio from 1 to 21, or null if either colour is incomplete.
export function contrastRatio(a, b) {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  if (la === null || lb === null) return null
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

// Below 3:1 text is hard to read at any size, so that's where we warn.
export const isLowContrast = ratio => ratio !== null && ratio < 3
